import { cn } from '@core/utils';
import { useEffect, useRef, useState } from 'react';

import type { HTMLTagProps, Size } from '@/types';

export type InputWrapProps = Omit<HTMLTagProps<'div'>, 'size'> & {
  size?: Size | 'fit-content';
  validationMessage?: string;
  message?: string;
  reversed?: boolean;
  label?: string;
  required?: boolean;
  shape?: 'rectangle' | 'capsule';
  isInvalid?: boolean;
};

export const InputWrap = ({
  //* Input.Wrap props
  size = 'large',
  validationMessage,
  message,
  reversed = false,
  label,
  required = false,
  isInvalid = false,
  shape = 'rectangle',

  //* HTML div props
  children,
  className,
  ...restDivProps
}: InputWrapProps) => {
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const [messageWrapHeight, setMessageWrapHeight] =
    useState<React.CSSProperties>();

  const hasMessage = !!(validationMessage || message);

  useEffect(() => {
    const { current } = messageContainerRef;
    if (current && hasMessage) {
      return setMessageWrapHeight({
        height: current.clientHeight,
      });
    }

    return setMessageWrapHeight(undefined);
  }, [hasMessage]);

  return (
    <div {...restDivProps} className={cn('relative w-full', className)}>
      <label className="box-border flex w-full flex-col transition-[box-shadow,background-color] duration-300">
        {label ? (
          <p className={cn('mb-8 text-[0.875rem] text-Gray-800', required && "after:text-[red] after:content-['_*']")}>
            {label}
          </p>
        ) : null}
        <div
          className={cn(
            'relative box-border flex w-full items-center justify-center gap-[0.8em] border border-Gray-400 px-16 text-Gray-900 transition-[color,background-color,border-color] duration-300',
            size === 'small' && 'h-32 text-[0.75rem]',
            size === 'medium' && 'h-40 text-[0.875rem]',
            size === 'large' && 'h-48 text-[1rem]',
            size === 'fit-content' && 'h-fit p-0',
            reversed && 'flex-row-reverse',
            shape === 'rectangle' && 'rounded-xsmall',
            shape === 'capsule' && 'rounded-pill',
            'has-[:disabled]:border-Gray-300 has-[:disabled]:bg-Gray-300 has-[:disabled]:text-Gray-500',
            'not-has-[:disabled]:bg-Gray-white not-has-[:disabled]:has-[:focus]:border-[#8c919f]',
            (validationMessage || isInvalid) && 'border-[#fc3933] has-[:disabled]:border-transparent',
          )}
        >
          {children}
        </div>
      </label>
      <div
        className={cn(
          'h-0 pt-0 transition-[height,padding-top] duration-300',
          messageWrapHeight && 'pt-4',
        )}
        style={messageWrapHeight}
      >
        {hasMessage ? (
          <div
            ref={messageContainerRef}
            className="mx-2 flex justify-between gap-[0.8em] text-[0.75rem] text-Gray-600"
          >
            <span className="animate-shake animate-duration-[900ms]">
              {validationMessage}
            </span>
            <span>{message}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
};
